import React, { useState, useEffect } from "react";
import { ArrowRightIcon } from "lucide-react";
import { useSidebar } from "~/components/SidebarLayout/SidebarContext";
import { useWindowSize } from "~/utils/useWindowSize";
import * as Analytics from "~/modules/dashboard_page_unlocks/analytics";
import { usePageScrollLock } from "~/hooks/use-page-scroll-lock";
import { useScrollThreshold } from "~/hooks/use-scroll-threshold";
import { InlineAuthForm } from "~/modules/auth/components/InlineAuthForm";
import { useInlineAuth } from "~/modules/auth/hooks/useInlineAuth";
import { LOGIN_TO_UNLOCK_DRAWER_BENEFITS } from "~/modules/dashboard_page_unlocks/consts";
import {
    DrawerBackdrop,
    DrawerContent,
    DrawerFooter,
    DrawerFooterItem,
    DrawerHeader,
    DrawerSheet,
    DrawerSubheader,
} from "~/components/Drawer";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

// // // //

/**
 * LoginToUnlockDrawer
 * Bottom drawer shown on dashboard pages that require an account to users who
 * aren't logged in. Mirrors the `UnlockPageDrawer` / `PaywallDrawer` look and
 * behavior (appears after scrolling 30% of the page) and prompts the user to log in or
 * create an account via an inline auth form.
 *
 * NOTE - only render this for logged-out users; it performs no auth checks.
 */
export function LoginToUnlockDrawer() {
    const [isVisible, setIsVisible] = useState(false);
    const [showAuthForm, setShowAuthForm] = useState(false);
    const [authVariant, setAuthVariant] = useState<"login" | "register">(
        "register",
    );
    const { sidebarWidth } = useSidebar();

    const {
        currentStep,
        isLoading,
        userEmail,
        errorMessage,
        handleEmailFormSubmit,
        handleGoogleSignIn,
        handleOTPSubmit,
    } = useInlineAuth({});

    const { isLg, isXl, is2xl } = useWindowSize();
    const isLargeScreen = isLg || isXl || is2xl;

    // Show the drawer after scrolling 30% down the page
    useScrollThreshold({
        thresholdPercent: 30,
        onThresholdCrossed: () => setIsVisible(true),
    });

    // Lock page scroll while the drawer is visible
    usePageScrollLock({ lockScroll: isVisible });

    // Track once when the gate is rendered - independent of the scroll threshold
    // NOTE - mount-only on purpose, so later prop changes don't re-fire the event
    useEffect(() => {
        Analytics.trackLoginToUnlockDrawerGateViewed();
    }, []);

    useEffect(() => {
        if (isVisible) {
            Analytics.trackLoginToUnlockDrawerShown();
        }
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <LoginToUnlockDrawerView
            isLargeScreen={isLargeScreen}
            sidebarWidth={sidebarWidth}
            showAuthForm={showAuthForm}
            authVariant={authVariant}
            onCreateAccountClick={() => {
                // If the form is already open, this is a swap from login -> register
                if (showAuthForm) {
                    Analytics.trackLoginToUnlockDrawerChangeAuthVariant({
                        prevValue: authVariant,
                        nextValue: "register",
                    });
                } else {
                    Analytics.trackLoginToUnlockDrawerCreateAccountClick();
                }
                setAuthVariant("register");
                setShowAuthForm(true);
            }}
            onSignInClick={() => {
                // If the form is already open, this is a swap from register -> login
                if (showAuthForm) {
                    Analytics.trackLoginToUnlockDrawerChangeAuthVariant({
                        prevValue: authVariant,
                        nextValue: "login",
                    });
                } else {
                    Analytics.trackLoginToUnlockDrawerSignInClick();
                }
                setAuthVariant("login");
                setShowAuthForm(true);
            }}
            inlineAuth={{
                currentStep,
                isLoading,
                userEmail,
                errorMessage,
                onGoogleSignIn: handleGoogleSignIn,
                onEmailSubmit: handleEmailFormSubmit,
                onOTPSubmit: handleOTPSubmit,
            }}
        />
    );
}

// // // //

/**
 * Expandable
 * Animates its children open/closed by transitioning the grid row between
 * 0fr and 1fr. Collapsed content is inert so it can't be focused or clicked.
 */
function Expandable(props: { expanded: boolean; children: React.ReactNode }) {
    const { expanded, children } = props;

    return (
        <div
            className={cn(
                "grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none",
                {
                    "grid-rows-[1fr] opacity-100": expanded,
                    "grid-rows-[0fr] opacity-0": !expanded,
                },
            )}
            aria-hidden={!expanded}
            inert={!expanded}
        >
            <div className="min-h-0 overflow-hidden">{children}</div>
        </div>
    );
}

// // // //

export function LoginToUnlockDrawerView(props: {
    isLargeScreen: boolean;
    sidebarWidth: number;
    showAuthForm: boolean;
    authVariant: "login" | "register";
    onCreateAccountClick: () => void;
    onSignInClick: () => void;
    inlineAuth: {
        currentStep: "auth" | "otp";
        isLoading: boolean;
        userEmail: string;
        errorMessage: string | null;
        onGoogleSignIn: () => void;
        onEmailSubmit: (email: string) => void;
        onOTPSubmit: (code: string, email: string) => void;
    };
}) {
    const {
        isLargeScreen,
        sidebarWidth,
        showAuthForm,
        authVariant,
        onCreateAccountClick,
        onSignInClick,
        inlineAuth,
    } = props;

    return (
        <>
            <DrawerBackdrop />

            {/* Drawer - positioned to avoid sidebar on large screens */}
            <DrawerSheet offsetLeft={isLargeScreen ? sidebarWidth : 0}>
                <DrawerContent>
                    {/* Headline */}
                    <DrawerHeader>Log in to unlock this page</DrawerHeader>

                    {/* Subheading */}
                    <DrawerSubheader>
                        Create a free RWA.xyz account or log in to continue
                        exploring real-world asset tokenization data.
                    </DrawerSubheader>

                    <div className="flex flex-col justify-center">
                        <Expandable expanded={!showAuthForm}>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                                <Button
                                    type="button"
                                    onClick={onCreateAccountClick}
                                    variant="primaryLight"
                                    size="lg"
                                    className="rounded-full text-base"
                                >
                                    Create account
                                    <ArrowRightIcon />
                                </Button>
                            </div>

                            {/* Fine print */}
                            <p className="text-sm text-muted-foreground mt-5 font-sans">
                                Already a member?{" "}
                                <button
                                    type="button"
                                    onClick={onSignInClick}
                                    className="text-foreground underline hover:no-underline"
                                >
                                    Sign in
                                </button>
                            </p>
                        </Expandable>

                        <Expandable expanded={showAuthForm}>
                            <InlineAuthForm
                                variant={authVariant}
                                currentStep={inlineAuth.currentStep}
                                isLoading={inlineAuth.isLoading}
                                userEmail={inlineAuth.userEmail}
                                errorMessage={inlineAuth.errorMessage}
                                onGoogleSignIn={inlineAuth.onGoogleSignIn}
                                onEmailSubmit={inlineAuth.onEmailSubmit}
                                onOTPSubmit={inlineAuth.onOTPSubmit}
                            />

                            {/* Swap between login and register */}
                            <p className="text-sm text-muted-foreground mt-5 font-sans">
                                {authVariant === "register" ? (
                                    <>
                                        Already a member?{" "}
                                        <button
                                            type="button"
                                            onClick={onSignInClick}
                                            className="text-foreground underline hover:no-underline"
                                        >
                                            Sign in
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        Don&apos;t have an account?{" "}
                                        <button
                                            type="button"
                                            onClick={onCreateAccountClick}
                                            className="text-foreground underline hover:no-underline"
                                        >
                                            Create one
                                        </button>
                                    </>
                                )}
                            </p>
                        </Expandable>
                    </div>

                    {/* Benefits */}
                    <DrawerFooter>
                        {LOGIN_TO_UNLOCK_DRAWER_BENEFITS.map((benefit) => (
                            <DrawerFooterItem key={benefit} showCheckmark>
                                {benefit}
                            </DrawerFooterItem>
                        ))}
                    </DrawerFooter>
                </DrawerContent>
            </DrawerSheet>
        </>
    );
}
